import { EventAvailable, EventBusy, ExpandMore, Info, PersonPin } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Grid, Typography } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers';
import { QueryObserverResult } from '@tanstack/react-query';
import React, { useState } from 'react';
import { EvacuationData } from '../../api/types';
import { AdamsError } from '../../utils/errors';
import { useTranslations } from '../Translations/TranslationsProvider';
import { EditEvacuationDialog } from './EditEvacuationDialog';

interface EventDetailsProps {
    event: EvacuationData;
    refetchEvacuation: () => Promise<QueryObserverResult<EvacuationData, AdamsError>>;
}

export const EvacuationDetails: React.FC<EventDetailsProps> = ({ event, refetchEvacuation }) => {
    const { translations } = useTranslations();

    const [openEditDialog, setOpenEditDialog] = useState(false);

    const handleOpenEditDialog = () => {
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
    };

    return (
        <>
            <Accordion data-testid="event-details-accordion" sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">{translations.EVACUATIONS_DETAILS}</Typography>
                </AccordionSummary>

                <Divider />

                <AccordionDetails>
                    <Box display={'flex'} flexDirection={'column'} gap={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {event.eventStatus === 'Ongoing' ? <EventAvailable /> : <EventBusy />}
                            <Typography variant="body2">{event.eventTitle}</Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                            <Info />
                            <Typography variant="body2">{event.eventDescription}</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6} display="flex" alignItems="center">
                                <CalendarIcon sx={{ mr: 1 }} />
                                <Typography variant="body2" fontWeight="medium">
                                    {translations.EVACUATIONS_START}
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {event.eventStartDate}
                                </Typography>
                            </Grid>

                            {event.eventEndDate && event.eventEndDate !== '-' && (
                                <Grid item xs={12} md={6} display="flex" alignItems="center">
                                    <CalendarIcon sx={{ mr: 1 }} />
                                    <Typography variant="body2" fontWeight="medium">
                                        {translations.EVACUATIONS_END}
                                    </Typography>
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        {event.eventEndDate}
                                    </Typography>
                                </Grid>
                            )}

                            <Grid item xs={12} md={6} display="flex" alignItems="center">
                                <PersonPin sx={{ mr: 1 }} />
                                <Typography variant="body2">
                                    {event.creatorFirstName} {event.creatorLastName}, {event.creatorOrgUnit}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box display="flex" alignItems="center" justifyContent={'center'}>
                            <Button
                                data-testid="event-edit-button"
                                fullWidth
                                onClick={handleOpenEditDialog}
                                variant={'contained'}
                            >
                                {translations.EVACUATIONS_EDIT_BUTTON}
                            </Button>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <EditEvacuationDialog
                event={event}
                openEditDialog={openEditDialog}
                handleCloseEditDialog={handleCloseEditDialog}
                refetchEvacuation={refetchEvacuation}
            />
        </>
    );
};
